import React from 'react';
import { Button } from './ui/button';
import DateRangePicker from './DateRangePicker';
import {
  Briefcase, GraduationCap, BookOpen, Plus, X, ChevronDown, ChevronUp
} from 'lucide-react';

export const PresentationSection = ({ profile, isEditing, editData, setEditData }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6" data-testid="section-presentation">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
        <BookOpen className="h-5 w-5 mr-2 text-purple-500" />Présentation
      </h2>
    </div>
    {isEditing ? (
      <textarea value={editData.bio || ''} onChange={(e) => setEditData({ ...editData, bio: e.target.value })} placeholder="Présentez-vous en quelques mots..." className="w-full h-32 p-3 border rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-purple-500 outline-none resize-none dark:bg-gray-900 dark:border-gray-700 dark:text-white" />
    ) : (
      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{profile.bio || "Aucune présentation pour le moment."}</p>
    )}
  </div>
);

export const ExperienceSection = ({
  profile, isEditing, editData, showAll, setShowAll,
  onAdd, onRemove, onUpdate
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6" data-testid="section-experience">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
        <Briefcase className="h-5 w-5 mr-2 text-cyan-500" />Expérience professionnelle
      </h2>
      {isEditing && (<Button size="sm" variant="ghost" onClick={onAdd}><Plus className="h-4 w-4 mr-1" />Ajouter</Button>)}
    </div>
    
    {(isEditing ? editData.experience : profile.experience)?.length > 0 ? (
      <div className="space-y-6">
        {(isEditing ? editData.experience : profile.experience)
          .slice(0, isEditing || showAll ? undefined : 3)
          .map((exp, index) => (
          <div key={index} className="relative pl-6 border-l-2 border-cyan-200 dark:border-cyan-800">
            {isEditing ? (
              <div className="space-y-3">
                <button onClick={() => onRemove(index)} className="absolute -left-3 top-0 w-6 h-6 bg-red-100 text-red-500 rounded-full flex items-center justify-center hover:bg-red-200"><X className="h-3 w-3" /></button>
                <input type="text" value={exp.title || ''} onChange={(e) => onUpdate(index, 'title', e.target.value)} placeholder="Titre du poste" className="w-full font-semibold text-gray-900 dark:text-white bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-purple-500 outline-none pb-1" />
                <input type="text" value={exp.company || ''} onChange={(e) => onUpdate(index, 'company', e.target.value)} placeholder="Entreprise" className="w-full text-purple-600 dark:text-purple-400 bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-purple-500 outline-none pb-1" />
                <DateRangePicker value={exp.years || ''} onChange={(v) => onUpdate(index, 'years', v)} />
                <textarea value={exp.description || ''} onChange={(e) => onUpdate(index, 'description', e.target.value)} placeholder="Description..." className="w-full text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 rounded-lg p-2 border dark:border-gray-700 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 outline-none resize-none" rows={2} />
              </div>
            ) : (
              <>
                <div className="absolute -left-2.5 top-0 w-4 h-4 bg-cyan-500 rounded-full"></div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{exp.title}</h3>
                <p className="text-purple-600 dark:text-purple-400 font-medium">{exp.company}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{exp.years}</p>
                {exp.description && <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">{exp.description}</p>}
              </>
            )}
          </div>
        ))}
        
        {!isEditing && profile.experience?.length > 3 && (
          <button onClick={() => setShowAll(!showAll)} className="w-full mt-4 py-2 text-sm font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors flex items-center justify-center gap-1">
            {showAll ? (<><ChevronUp className="h-4 w-4" /> Voir moins</>) : (<><ChevronDown className="h-4 w-4" /> Voir {profile.experience.length - 3} expérience{profile.experience.length - 3 > 1 ? 's' : ''} de plus</>)}
          </button>
        )}
      </div>
    ) : (
      <p className="text-gray-400 dark:text-gray-500 text-center py-4">{isEditing ? "Cliquez sur 'Ajouter' pour ajouter une expérience" : "Aucune expérience renseignée"}</p>
    )}
  </div>
);

export const EducationSection = ({
  profile, isEditing, editData, showAll, setShowAll,
  onAdd, onRemove, onUpdate
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6" data-testid="section-education">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
        <GraduationCap className="h-5 w-5 mr-2 text-pink-500" />Formation
      </h2>
      {isEditing && (<Button size="sm" variant="ghost" onClick={onAdd}><Plus className="h-4 w-4 mr-1" />Ajouter</Button>)}
    </div>
    
    {(isEditing ? editData.education : profile.education)?.length > 0 ? (
      <div className="space-y-6">
        {(isEditing ? editData.education : profile.education)
          .slice(0, isEditing || showAll ? undefined : 3)
          .map((edu, index) => (
          <div key={index} className="relative pl-6 border-l-2 border-pink-200 dark:border-pink-800">
            {isEditing ? (
              <div className="space-y-3">
                <button onClick={() => onRemove(index)} className="absolute -left-3 top-0 w-6 h-6 bg-red-100 text-red-500 rounded-full flex items-center justify-center hover:bg-red-200"><X className="h-3 w-3" /></button>
                <input type="text" value={edu.degree || ''} onChange={(e) => onUpdate(index, 'degree', e.target.value)} placeholder="Diplôme" className="w-full font-semibold text-gray-900 dark:text-white bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-purple-500 outline-none pb-1" />
                <input type="text" value={edu.school || ''} onChange={(e) => onUpdate(index, 'school', e.target.value)} placeholder="École / Université" className="w-full text-pink-600 dark:text-pink-400 bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-purple-500 outline-none pb-1" />
                <DateRangePicker value={edu.years || edu.year || ''} onChange={(v) => onUpdate(index, 'years', v)} />
              </div>
            ) : (
              <>
                <div className="absolute -left-2.5 top-0 w-4 h-4 bg-pink-500 rounded-full"></div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{edu.degree}</h3>
                <p className="text-pink-600 dark:text-pink-400 font-medium">{edu.school}</p>
                {(edu.years || edu.year) && <p className="text-sm text-gray-500 dark:text-gray-400">{edu.years || edu.year}</p>}
              </>
            )}
          </div>
        ))}
        
        {!isEditing && profile.education?.length > 3 && (
          <button onClick={() => setShowAll(!showAll)} className="w-full mt-4 py-2 text-sm font-medium text-pink-600 hover:text-pink-700 hover:bg-pink-50 dark:hover:bg-pink-900/20 rounded-lg transition-colors flex items-center justify-center gap-1">
            {showAll ? (<><ChevronUp className="h-4 w-4" /> Voir moins</>) : (<><ChevronDown className="h-4 w-4" /> Voir {profile.education.length - 3} formation{profile.education.length - 3 > 1 ? 's' : ''} de plus</>)}
          </button>
        )}
      </div>
    ) : (
      <p className="text-gray-400 dark:text-gray-500 text-center py-4">{isEditing ? "Cliquez sur 'Ajouter' pour ajouter une formation" : "Aucune formation renseignée"}</p>
    )}
  </div>
);
